import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledJournalEntryTabContainer} from '../../../NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import TableFilters from '../../TableFilters';
import EmptyOrTable from '../../../../EmptyOrTable';
import {accountingHelpers, formHelpers} from '../../../../../utils/helpers';
import {useAccountingOptions} from '../../../../../hooks';

const dateColumnProps = accountingHelpers.getDateColumnProps();

const defaultFilterProps = accountingHelpers.getHistoryTabFilterProps();

const BillsHistoryTab = ({
  data = [],
  empty,
  loading = false,
  onFilter,
  onRowUpdate,
  tableFiltersProps,
  tableProps,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [query, setQuery] = useState({ search: '' });

  const {
    departmentOptions,
    listItemOptions,
    locationOptions,
    taxCodeOptions
  } = useAccountingOptions();

  const handleOnFilter = (filterParams) => {
    setQuery({...query, ...filterParams});
    onFilter?.(filterParams);
  }

  const getInput = ({ defaultValue, propName, record }) => {
    return accountingHelpers.isBillInvoiceSyncStatusError(record?.sync_status)
      ? accountingHelpers.getTableInput({
          defaultValue,
          propName,
          record,
          t,
          onChange: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
        })
      : defaultValue;
  };

  const getSelect = ({ defaultValue, options, propName, record, ...rest }) => {
    return accountingHelpers.isBillInvoiceSyncStatusError(record?.sync_status)
      ? accountingHelpers.getTableSelect({
          defaultValue,
          options,
          propName,
          record,
          t,
          onSelect: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
          ...rest
        })
      : formHelpers.getOptionValueLabel(options, defaultValue);
  };

  const columns = [
    { dataIndex: 'remote_id', title: t('billReference') },
    { ...dateColumnProps, dataIndex: 'posting_date', title: `${t('posting')} ${t('date')}` },
    {
      ...dateColumnProps,
      dataIndex: 'invoice_date',
      title: `${t('invoice')} ${t('date')}`,
      render: (_, invoice) => accountingHelpers.getBillInvoiceDateWithLink({invoice, t}),
    },
    {
      dataIndex: 'id',
      title: t('invoice'),
      render: (value) => accountingHelpers.renderInvoicePreview(value, 'accounting')
    },
    { dataIndex: 'sync_status', title: t('status'), render: accountingHelpers.getSyncStatus },
    { ...dateColumnProps, dataIndex: 'due_date', title: `${t('due')} ${t('date')}` },
    {
      align: 'right',
      dataIndex: 'total_amount',
      render: accountingHelpers.getBillAmount,
      title: t('amount'),
    },
    { dataIndex: 'uploaded_by', title: t('uploadedBy') },
    { dataIndex: 'vendor_id', title: t('vendor'), render: accountingHelpers.getBillVendorName },
    {
      dataIndex: 'memo',
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'memo', record }),
      title: t('memo'),
    },
    {
      dataIndex: 'department',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: departmentOptions, propName: 'department' }),
      title: t('department'),
    },
    {
      dataIndex: 'location',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: locationOptions, propName: 'location' }),
      title: t('location'),
    },
    {
      dataIndex: 'tax_code',
      render: (_, record) => getSelect({ defaultValue: accountingHelpers.getBillTaxCode(record), record, options: taxCodeOptions, propName: 'tax_code' }),
      title: t('taxCode'),
    },
    {
      dataIndex: 'list_item',
      render: (_, record) => getSelect({ defaultValue: accountingHelpers.getBillListItem(record), record, options: listItemOptions, propName: 'line_item', placeholderPropName: 'listItem' }),
      title: t('listItem'),
    }
  ];

  return (
    <StyledJournalEntryTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        {...defaultFilterProps}
        onFilter={handleOnFilter}
      />
      <EmptyOrTable
        data={data}
        empty={empty}
        loading={loading}
        query={query}
        columns={columns}
        tableProps={tableProps}
      />
    </StyledJournalEntryTabContainer>
  );
}

BillsHistoryTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  onFilter: PropTypes.func,
  onRowUpdate: PropTypes.func,
  selectedRowsIds: PropTypes.array,
  tableFiltersProps: PropTypes.any,
  tableProps: PropTypes.object,
}

export default BillsHistoryTab;
